import * as React from "react";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CallIcon from "@mui/icons-material/Call";
import CampaignIcon from "@mui/icons-material/Campaign";
import ChatIcon from "@mui/icons-material/Chat";
import ContactEmergencyIcon from "@mui/icons-material/ContactEmergency";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FaxIcon from "@mui/icons-material/Fax";
import InsightsIcon from "@mui/icons-material/Insights";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StarIcon from "@mui/icons-material/Star";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import TodayIcon from "@mui/icons-material/Today";

import { QBSyncIcon } from "components/icons";
import {
  DASHBOARD_FEATURE_FLAG,
  DISABLE_REPORTS_AND_DASHBOARD,
  ERA_CLAIMS_TAB_FEATURE,
  FAXES_PORTAL_FEATURE,
  IMPLEMENTATION_HUB_FEATURE,
  INTERNAL_DOCUMENTS_FEATURE,
  LEADS_UI,
  MARKETING_AUTOMATION_FEATURE,
  MARKETPLACE_FEATURE,
  MEDICAL_SCRIBE_FEATURE,
  SALES_COACH_DASHBOARD_FEATURE,
  SCRIBE_FOLLOW_UPS_FEATURE,
  VOB_REQUEST_FEATURE
} from "constants/feature-flags";
import { VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS } from "constants/permissions";
import {
  ADMIN_LEVEL_GROUPS,
  GROUP_ADMINISTRATORS,
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS,
  GROUP_SUNO
} from "constants/staff";
import { SUPADEMO_SETTINGS_DEMO_ID } from "constants/supa-demo";

/**
 * Practice-level boolean columns a menu item may gate on. Narrowed to the ones
 * actually in use so `currentPractice?.[practiceFlag]` stays a checked index.
 */
export type MenuPracticeFlag =
  | "implementation_hub_visible"
  | "training_enabled"
  | "google_reviews_enabled";

/** User-level boolean columns a menu item may gate on. */
export type MenuUserFlag = "has_access_to_google_reviews";

/**
 * The side menu's group headings, in display order (ENG-2535). Home has no
 * group and sits between "Getting started" and "Front desk"; Settings is
 * pinned in the footer and has none either.
 */
export const MENU_GROUPS = [
  "Getting started",
  "Front desk",
  "Billing",
  "Growth",
  "Scribe",
  "Practice"
] as const;

export type MenuGroup = (typeof MENU_GROUPS)[number];

export interface MenuItemConfig {
  to: string;
  primary: string;
  Icon: React.ElementType;
  isFallbackPath: boolean;
  tooltip?: string;
  roles?: string[];
  permissions?: string[];
  featureFlag?: string;
  practiceFlag?: MenuPracticeFlag;
  userFlag?: MenuUserFlag;
  hideFlag?: string;
  supaDemoId?: string;
  /** Heading the side menu lists this under; undefined = Home or the footer. */
  group?: MenuGroup;
  /** Pinned under the scrolling list (Settings), always in view. */
  footer?: boolean;
}

/**
 * The app's primary navigation, in display order.
 *
 * Lives in its own module (rather than in `layout-side-menu.component.tsx`,
 * where it used to be exported from) so that `use-visible-menu-items.hook.ts`
 * can read it without closing an import cycle back through the component.
 * `layout-side-menu/index.ts` still re-exports `MENU`, so existing importers
 * are unaffected.
 */
export const MENU: MenuItemConfig[] = [
  {
    to: "/onboarding",
    primary: "Onboarding",
    group: "Getting started",
    Icon: RocketLaunchOutlinedIcon,
    isFallbackPath: false,
    featureFlag: IMPLEMENTATION_HUB_FEATURE,
    practiceFlag: "implementation_hub_visible",
    tooltip: "Track your onboarding journey"
  },
  {
    to: "/practice-sessions",
    primary: "Practice Sessions",
    group: "Getting started",
    Icon: SchoolRoundedIcon,
    isFallbackPath: false,
    // No featureFlag — training_enabled (a Practice-level field, Suno-group-write-only) is the
    // sole gate, deliberately independent of IMPLEMENTATION_HUB_FEATURE/implementation_hub_visible
    // above (ENG-1832 D1/D3/D7).
    practiceFlag: "training_enabled",
    tooltip: "Complete guided training scenarios"
  },
  {
    to: "/scheduling",
    primary: "Scheduling",
    group: "Front desk",
    Icon: TodayIcon,
    isFallbackPath: true,
    tooltip: "Schedule & manage patient appointments"
  },
  {
    // ENG-1981: development access only (`scheduling-v-3-dev-access`, via
    // `useSchedulerChoice`). Everyone else reaches V3 through the Scheduling entry above,
    // which follows their own choice.
    to: "/scheduling-v3",
    primary: "Scheduling V3",
    group: "Front desk",
    Icon: CalendarMonthOutlinedIcon,
    isFallbackPath: false,
    tooltip: "The new scheduler (development)"
  },
  {
    to: "/patients",
    primary: "Patients",
    group: "Front desk",
    Icon: PeopleAltIcon,
    isFallbackPath: false,
    tooltip: "Access & update patients’ medical & personal details"
  },
  {
    to: "/inbox",
    primary: "Inbox",
    group: "Front desk",
    Icon: QuestionAnswerIcon,
    isFallbackPath: false,
    tooltip: "Manage SMS communications with patients"
  },
  {
    to: "/chat",
    primary: "Chat",
    group: "Front desk",
    Icon: ChatIcon,
    isFallbackPath: false,
    tooltip: "In-App Chat"
  },
  {
    to: "/calls",
    primary: "Calls",
    group: "Front desk",
    Icon: CallIcon,
    isFallbackPath: false,
    // No roles: Voice access, not a role, opens /calls (ENG-2315). The rule
    // lives in the visibility filter so it matches the /calls/* route gate and
    // nobody sees a dead link.
    tooltip: "Log and manage patient calls & follow-ups"
  },
  {
    to: "/faxes-portal",
    primary: "Faxes",
    group: "Front desk",
    Icon: FaxIcon,
    isFallbackPath: false,
    featureFlag: FAXES_PORTAL_FEATURE,
    tooltip: "View and manage faxes"
  },
  {
    to: "/billing",
    primary: "Billing",
    group: "Billing",
    Icon: RequestQuoteOutlinedIcon,
    isFallbackPath: false,
    tooltip: "Manage invoices & payment details"
  },
  {
    to: "/collections",
    primary: "Collect",
    group: "Billing",
    Icon: AccountBalanceWalletIcon,
    isFallbackPath: false,
    tooltip: "Manage outstanding patient balances & payment recovery"
  },
  {
    to: "/eras",
    primary: "ERAs",
    group: "Billing",
    Icon: SummarizeOutlinedIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    featureFlag: ERA_CLAIMS_TAB_FEATURE,
    tooltip: "View & manage insurance claims & payments"
  },
  {
    to: "/vobs",
    primary: "VOBs",
    group: "Billing",
    Icon: AssignmentIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    featureFlag: VOB_REQUEST_FEATURE,
    tooltip: "View Verification of Benefits requests"
  },
  {
    to: "/quickbooks",
    primary: "QuickBooks",
    group: "Billing",
    Icon: QBSyncIcon as React.ElementType,
    isFallbackPath: false,
    tooltip: "QuickBooks settings"
  },
  {
    to: "/marketing",
    primary: "Marketing",
    group: "Growth",
    Icon: CampaignIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    featureFlag: MARKETING_AUTOMATION_FEATURE,
    tooltip: "Plan & track campaigns & patient touchpoints"
  },
  {
    to: "/leads",
    primary: "Leads",
    group: "Growth",
    Icon: ContactEmergencyIcon,
    isFallbackPath: false,
    tooltip: "Suno Leads Application",
    featureFlag: LEADS_UI
  },
  {
    to: "/reviews",
    primary: "Reviews",
    group: "Growth",
    Icon: StarIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS, GROUP_ADMINISTRATORS, GROUP_AUDIOLOGISTS],
    practiceFlag: "google_reviews_enabled",
    userFlag: "has_access_to_google_reviews",
    tooltip: "Manage Google reviews"
  },
  {
    to: "/medical-scribes",
    primary: "Scribes",
    group: "Scribe",
    Icon: AudioFileIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS, GROUP_SUNO, GROUP_AUDIOLOGISTS],
    featureFlag: MEDICAL_SCRIBE_FEATURE,
    tooltip: "Medical Scribes"
  },
  // Roles mirror Scribes above — this reads the same recordings, so anyone who
  // can see one should see the other. Takes the declarative featureFlag
  // fallthrough in the filter, so no `to === ...` special case is needed.
  {
    to: "/sales-coach",
    primary: "Sales Coach",
    group: "Scribe",
    Icon: InsightsIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS, GROUP_SUNO, GROUP_AUDIOLOGISTS],
    featureFlag: SALES_COACH_DASHBOARD_FEATURE,
    tooltip: "How opportunity consultations are being handled"
  },
  {
    to: "/action-items",
    primary: "Action Items",
    group: "Scribe",
    Icon: AutoAwesomeIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS, GROUP_SUNO, GROUP_AUDIOLOGISTS],
    featureFlag: SCRIBE_FOLLOW_UPS_FEATURE,
    tooltip: "Action items the model found in scribe recordings"
  },
  {
    to: "/dashboard",
    primary: "Dashboard",
    group: "Practice",
    Icon: DashboardIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS],
    featureFlag: DASHBOARD_FEATURE_FLAG,
    hideFlag: DISABLE_REPORTS_AND_DASHBOARD,
    tooltip: "View key Practice metrics & summaries"
  },
  {
    to: "/reports",
    primary: "Reports",
    group: "Practice",
    Icon: BarChartIcon,
    isFallbackPath: false,
    roles: ADMIN_LEVEL_GROUPS,
    permissions: [VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS],
    hideFlag: DISABLE_REPORTS_AND_DASHBOARD,
    tooltip: "View & generate detailed reports"
  },
  {
    to: "/inventory",
    primary: "Inventory",
    group: "Practice",
    Icon: ContentPasteSearchIcon,
    isFallbackPath: false,
    tooltip: "Manage & track device/accessory status, orders, & stock"
  },
  {
    to: "/marketplace",
    primary: "Marketplace",
    group: "Practice",
    Icon: StorefrontIcon,
    isFallbackPath: false,
    roles: [GROUP_BUSINESS_OWNERS],
    featureFlag: MARKETPLACE_FEATURE,
    tooltip: "Browse & enable integrations"
  },
  {
    to: "/sops",
    primary: "SOPs",
    // Last in Practice, so it sits directly above the pinned Settings footer.
    group: "Practice",
    Icon: MenuBookIcon,
    isFallbackPath: false,
    featureFlag: INTERNAL_DOCUMENTS_FEATURE,
    tooltip: "Your practice's SOPs and internal documents"
  },
  {
    to: "/settings",
    primary: "Settings",
    footer: true,
    Icon: SettingsOutlinedIcon,
    isFallbackPath: false,
    tooltip: "Customize clinic preferences & user settings",
    supaDemoId: SUPADEMO_SETTINGS_DEMO_ID
  }
];
